import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import {
  getFeatureGateMessage,
  getPendingSubscriptionRequest,
  hasPlanAccess,
  PLAN_IDS,
} from '../../utils/subscription';

const ProtectedRoute = ({ 
  children, 
  requireAuth = true, 
  requireApproval = false,
  requiredRole = null,
  requiredPlan = PLAN_IDS.FREE,
  redirectTo = '/login'
}) => {
  const { 
    isAuthenticated, 
    isLoading, 
    userData, 
    hasRole, 
    isApproved 
  } = useAuthStore();
  const location = useLocation();
  const pendingSubscriptionRequest = getPendingSubscriptionRequest(userData);

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sigma-600"></div>
      </div>
    );
  }

  // Check authentication requirement
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check approval requirement
  if (requireApproval && !isApproved()) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Account Pending Approval
          </h2>
          <p className="text-gray-600">
            Your account is currently being reviewed. You'll receive an email notification once approved.
          </p>
        </div>
      </div>
    );
  }

  // Check role requirement
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  if (requireAuth && requiredPlan && !hasPlanAccess(userData, requiredPlan)) {
    const hasMatchingPendingPlan =
      pendingSubscriptionRequest &&
      requiredPlan !== PLAN_IDS.FREE &&
      pendingSubscriptionRequest.planId === requiredPlan;

    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
        <div className="text-center max-w-lg rounded-3xl border border-amber-200 bg-white p-8 shadow-sm">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
            <svg className="h-8 w-8 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 .552-.448 1-1 1s-1-.448-1-1a1 1 0 012 0zm6-3v8a2 2 0 01-2 2H8a2 2 0 01-2-2V8m10 0V6a4 4 0 10-8 0v2m8 0H8" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {hasMatchingPendingPlan ? 'Subscription Approval Pending' : 'Subscription Upgrade Required'}
          </h2>
          <p className="text-gray-600 mb-6">
            {hasMatchingPendingPlan
              ? 'Your paid-plan request is still waiting for admin approval. Access will unlock after approval.'
              : getFeatureGateMessage(requiredPlan)}
          </p>
          <NavigateButton />
        </div>
      </div>
    );
  }

  return children;
};

const NavigateButton = () => (
  <Link
    to="/pricing"
    className="inline-flex items-center justify-center rounded-xl bg-sigma-700 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-sigma-800"
  >
    View Plans
  </Link>
);

export default ProtectedRoute;
